// AnalyticsHeader.tsx
import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Typography, Box, Container, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { analyticsTabs, analyticsConfig } from './config/analyticsConfig';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3
  },
}));

const StyledTab = styled(Tab)(({ theme, color }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: 'rgba(0, 0, 0, 0.7)',
  '&.Mui-selected': {
    color: color || analyticsConfig.activeColor,
    fontWeight: theme.typography.fontWeightMedium,
    borderBottom: `3px solid ${color || analyticsConfig.activeColor}`,
  },
  '&:hover': {
    color: color || analyticsConfig.defaultColor,
    opacity: 1,
  },
}));

const HeaderContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  borderBottom: '1px solid #e8e8e8',
  marginBottom: theme.spacing(3)
}));

const TitleSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 0),
  backgroundColor: theme.palette.background.paper,
}));

function AnalyticsHeader({ colors = [], onTabChange, tabs = analyticsTabs }) {
  const [value, setValue] = useState(0);

  // The visible tab set is per-investigation (e.g. the Clinical tab only exists
  // with FUNCTIONALITY.CLINICAL). If it shrinks below the current selection
  // (switching to an investigation without that tab), snap back to the first tab
  // so the header and the rendered content stay in range and aligned.
  useEffect(() => {
    if (!tabs.length) {
      setValue(0);
      if (onTabChange) onTabChange(0);
      return;
    }
    if (value > tabs.length - 1) {
      setValue(0);
      if (onTabChange) onTabChange(0);
    }
  }, [tabs.length, value, onTabChange]);

  if (!tabs.length) {
    return null;
  }

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  const activeTab = tabs[value] || tabs[0];

  return (
    <HeaderContainer>
      <Container maxWidth="xl" sx={{ marginLeft: 0 }}>
        <TitleSection>
          <Typography variant="h3" component="h1" gutterBottom sx={{ color: activeTab.color }}>
            {activeTab.label}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {analyticsConfig.subtitle}
          </Typography>
        </TitleSection>
        <StyledTabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            style: { display: 'none' }
          }}
        >
          {tabs.map((tab) => (
            <StyledTab
              key={tab.id}
              label={tab.label}
              color={tab.color || analyticsConfig.defaultColor}
            />
          ))}
        </StyledTabs>
      </Container>
    </HeaderContainer>
  );
}

export default AnalyticsHeader;
